import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ClockIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const DoorIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-5 h-5 text-[#00213a] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const ExternalIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

const AttendancePolicyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main id="main-content" className="pt-0">
        {/* Hero */}
        <section
          className="relative pt-36 pb-20 bg-[#00213a] overflow-hidden"
        >
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0"
            style={{ height: '4rem', background: 'linear-gradient(to bottom, rgba(0,33,58,0) 0%, rgba(255,255,255,1) 100%)' }}
            aria-hidden="true"
          />

          <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                <span className="text-sm font-semibold text-white tracking-tight">Family Resources</span>
              </div>
            </div>
            <h1 className="text-5xl lg:text-7xl font-semibold text-white mb-6 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
              Attendance & School Hours
            </h1>
            <p className="text-2xl text-white/90 font-normal max-w-3xl leading-relaxed mx-auto">
              Everything you need to know about daily schedules, arrival, dismissal, and attendance at PS 96.
            </p>
          </div>
        </section>

        {/* Quick Hours Banner */}
        <section className="max-w-5xl mx-auto px-6 lg:px-8 -mt-6 mb-16 relative z-10">
          <div className="bg-[#fdbb2e] rounded-2xl px-8 py-6 flex flex-col sm:flex-row items-center justify-center gap-6 shadow-lg">
            <div className="flex items-center gap-3">
              <ClockIcon />
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-[#00213a]/70">School Hours</p>
                <p className="text-xl font-bold text-[#00213a]">8:00 AM – 2:20 PM</p>
              </div>
            </div>
            <div className="hidden sm:block w-px h-10 bg-[#00213a]/20" />
            <div className="flex items-center gap-3">
              <DoorIcon />
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-[#00213a]/70">Schoolyard Opens</p>
                <p className="text-xl font-bold text-[#00213a]">8:00 AM</p>
              </div>
            </div>
            <div className="hidden sm:block w-px h-10 bg-[#00213a]/20" />
            <div className="flex items-center gap-3">
              <ClockIcon />
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-[#00213a]/70">Late Arrival After</p>
                <p className="text-xl font-bold text-[#00213a]">8:10 AM</p>
              </div>
            </div>
          </div>
        </section>

        {/* Arrival & Dismissal */}
        <section className="max-w-5xl mx-auto px-6 lg:px-8 mb-20">
          <div className="mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#fdbb2e]/10 rounded-lg border border-[#fdbb2e]/30 mb-4">
              <span className="text-sm font-semibold text-[#00213a]">Daily Schedule</span>
            </div>
            <h2 className="text-4xl font-semibold text-gray-900 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
              Arrival & Dismissal Procedures
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Arrival */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#00213a] flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Arrival</h3>
              </div>

              <div className="space-y-6">
                <div>
                  <p className="text-xs font-black uppercase tracking-widest text-[#986801] mb-2">3K – 5th Grade</p>
                  <ul className="space-y-2">
                    {[
                      'Schoolyard opens at 8:00 AM on East 119th Street',
                      'Students enter through the schoolyard',
                      'Students arriving after 8:10 AM must enter through the main lobby',
                      'Students arriving after 8:10 AM will be marked late',
                      'Main lobby is closed to families between 8:00–8:10 AM',
                    ].map((point) => (
                      <li key={point} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckIcon />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="border-t border-gray-100 pt-6">
                  <p className="text-xs font-black uppercase tracking-widest text-[#986801] mb-2">6th – 8th Grade</p>
                  <ul className="space-y-2">
                    {[
                      'Enter through Exit 6 on East 120th Street (closest to 2nd Ave)',
                      'Exit 6 doors open at 8:00 AM',
                      'Students arriving after 8:10 AM must enter through the main lobby',
                      'Students arriving after 8:10 AM will be marked late',
                    ].map((point) => (
                      <li key={point} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckIcon />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Dismissal */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#fdbb2e] flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-[#00213a]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Dismissal — 2:20 PM</h3>
              </div>

              <div className="space-y-6">
                <div>
                  <p className="text-xs font-black uppercase tracking-widest text-[#986801] mb-2">3K – 5th Grade</p>
                  <ul className="space-y-2">
                    {[
                      'Dismissed in the schoolyard on East 119th Street',
                      'Main lobby closed to families 1:55–2:15 PM',
                      'All students must be picked up from the schoolyard before 2:15 PM',
                      'Students not picked up by 2:15 PM are escorted to the auditorium',
                    ].map((point) => (
                      <li key={point} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckIcon />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="border-t border-gray-100 pt-6">
                  <p className="text-xs font-black uppercase tracking-widest text-[#986801] mb-2">6th – 8th Grade</p>
                  <ul className="space-y-2">
                    {[
                      'Dismissed through Exit 6 on East 120th Street at 2:20 PM',
                      'Middle schoolers picking up siblings: provide written consent to main office in advance',
                    ].map((point) => (
                      <li key={point} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckIcon />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-800 font-medium">Students without a pick-up arrangement will wait in the auditorium until an adult arrives.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Attendance Policy */}
        <section className="bg-gray-50 py-20 mb-0">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <div className="mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#fdbb2e]/10 rounded-lg border border-[#fdbb2e]/30 mb-4">
                <span className="text-sm font-semibold text-[#00213a]">Attendance</span>
              </div>
              <h2 className="text-4xl font-semibold text-gray-900 mb-4 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
                The Importance of Regular Attendance
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl">
                Regular attendance at PS 96 plays a crucial role in your child's learning and academic success. Research shows that consistent attendance directly correlates with academic achievement.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: 'Attendance & Learning',
                  body: "The mornings are when the majority of critical learning takes place. Regular tardiness can significantly impact your child's educational progress for the current and subsequent school years.",
                },
                {
                  title: 'Scheduling Appointments',
                  body: 'Whenever possible, please schedule medical or other appointments outside of school hours. If unavoidable, send your child to school for part of the day — even half a day contributes positively.',
                },
                {
                  title: 'Reporting Absences',
                  body: 'If your child cannot attend school, contact the main office at 212-860-5851. An automated call, text, and email will be sent daily at 11:00 AM to notify guardians of absences.',
                },
                {
                  title: 'Attendance Records',
                  body: 'Morning attendance is recorded at 8:10 AM. Students arriving late will be marked absent initially but updated to "late" when afternoon attendance is taken at 2:00 PM.',
                },
              ].map((card) => (
                <div key={card.title} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                  <h3 className="text-base font-semibold text-[#00213a] mb-3">{card.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{card.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Quick Links */}
        <section className="bg-[#00213a] py-20">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <h2 className="text-3xl font-semibold text-white mb-10 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
              Quick Links
            </h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <a
                href="https://docs.google.com/document/d/1K39AKs00SNGaK4ReyjYOT3SbQSTWn0GGFDU7m0NxTWw/edit?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl p-6 transition-colors duration-200 group"
              >
                <div className="w-10 h-10 rounded-lg bg-[#fdbb2e] flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-[#00213a]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-white group-hover:text-[#fdbb2e] transition-colors">Frequently Asked Questions</p>
                  <p className="text-sm text-white/60 mt-1">Daily operations, systems, and structures at PS 96</p>
                  <div className="flex items-center gap-1 mt-2 text-xs text-white/70">
                    <ExternalIcon />
                    <span>Google Doc</span>
                  </div>
                </div>
              </a>

              <a
                href="https://stores.smoothusa.com/PS96ACT/shop/home"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl p-6 transition-colors duration-200 group"
              >
                <div className="w-10 h-10 rounded-lg bg-[#fdbb2e] flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-[#00213a]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-white group-hover:text-[#fdbb2e] transition-colors">Order School Uniforms</p>
                  <p className="text-sm text-white/60 mt-1">Order your child's PS 96 uniform online</p>
                  <div className="flex items-center gap-1 mt-2 text-xs text-white/70">
                    <ExternalIcon />
                    <span>smoothusa.com</span>
                  </div>
                </div>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AttendancePolicyPage;
